/* eslint-disable complexity */
import React from 'react';

import {cn} from '@bem-react/classname';
import {QuestionType, ValidationType} from 'common/constants/survey';
import {
    Question,
    QuestionValue,
    SurveyId,
    SurveyValues,
    Validation,
    ValidationWithValue,
} from 'common/models/survey';
import {
    ControllerFieldState,
    ControllerRenderProps,
    FieldPath,
    UseControllerProps,
    Validate,
} from 'react-hook-form';
import {
    validateDateRange,
    validateDecimal,
    validateEmail,
    validateINN,
    validateMax,
    validateMaxCount,
    validateMaxSize,
    validateMin,
    validatePhone,
    validateRegExp,
    validateRequired,
    validateRussian,
} from 'ui/utils/validations';

import QuestionSwitch from '../QuestionSwitch';

const cnQuestion = cn('Question');

export const renderQuestion = (question: Question, surveyId: SurveyId) => {
    return function renderQuestionSwitch({
        field,
        fieldState,
    }: {
        field: ControllerRenderProps<SurveyValues, FieldPath<SurveyValues>>;
        fieldState: ControllerFieldState;
    }) {
        return (
            <QuestionSwitch
                question={question}
                surveyId={surveyId}
                className={cnQuestion()}
                field={field}
                fieldState={fieldState}
            />
        );
    };
};

type Rules = UseControllerProps['rules'];

const defaultValidationsObj = {
    [ValidationType.Required]: false,
    [ValidationType.Min]: false,
    [ValidationType.Max]: false,
    [ValidationType.Size]: false,
    [ValidationType.Count]: false,
    [ValidationType.Decimal]: false,
    [ValidationType.Email]: false,
    [ValidationType.URL]: false,
    [ValidationType.Phone]: false,
    [ValidationType.INN]: false,
    [ValidationType.Russian]: false,
    [ValidationType.RegExp]: false,
};

export const getValidationRules = (
    questionType: QuestionType,
    validations?: Validation[],
): Rules => {
    const validationsObj: Record<ValidationType, boolean | number | string> = {
        ...defaultValidationsObj,
    };

    if (validations) {
        validations.forEach((validation) => {
            validationsObj[validation.type] = (validation as ValidationWithValue)?.value ?? true;
        });
    }

    const validateFuncs: Record<string, Validate<QuestionValue, SurveyValues>> = {};
    if (validationsObj.required) {
        validateFuncs.required = validateRequired;
    }
    if (validationsObj[ValidationType.Count]) {
        validateFuncs.count = validateMaxCount(Number(validationsObj.count));
    }
    if (validationsObj[ValidationType.Size]) {
        validateFuncs.size = validateMaxSize(Number(validationsObj.size));
    }
    if (validationsObj[ValidationType.Min]) {
        validateFuncs.min = validateMin(validationsObj.min);
    }
    if (validationsObj[ValidationType.Max]) {
        validateFuncs.max = validateMax(validationsObj.max);
    }
    if (validationsObj[ValidationType.Decimal]) {
        validateFuncs.decimal = validateDecimal;
    }
    if (validationsObj[ValidationType.RegExp]) {
        validateFuncs.regExp = validateRegExp(String(validationsObj.regexp));
    }
    if (validationsObj[ValidationType.Email]) {
        validateFuncs.email = validateEmail;
    }
    if (validationsObj[ValidationType.Russian]) {
        validateFuncs.russian = validateRussian;
    }
    if (validationsObj[ValidationType.INN]) {
        validateFuncs.inn = validateINN;
    }
    if (validationsObj[ValidationType.Phone]) {
        validateFuncs.phone = validatePhone;
    }
    if (questionType === QuestionType.DateRange) {
        validateFuncs.daterange = validateDateRange;
    }

    return {
        validate: validateFuncs,
    };
};
